import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { DatePicker } from '../utils';
import { useCurrency } from '../../utils/globalCurrency';
import downloadIcon from '../../assets/images/download.png';
import printIcon from '../../assets/images/share.png';

export interface TripleColumnItem {
  id: number | string;
  date: string;
  unit?: string;

  // Issue column (stock going out)
  issueQuantity?: number;
  issuePrice?: number;
  issueAmount?: number;

  // Receipt column (stock coming in)
  receiptQuantity?: number;
  receiptPrice?: number;
  receiptAmount?: number;

  // Balance column (current stock)
  balanceQuantity?: number;
  balancePrice?: number;
  balanceAmount?: number;
}

interface TripleColumnPageProps {
  title: string;
  description?: string;
  items: TripleColumnItem[];
  loading?: boolean;
  onItemClick?: (item: TripleColumnItem) => void;
  onExport?: () => void;
  onPrint?: () => void;
  onExportImage?: () => void;
  onExportPDF?: () => void;
  onExportExcel?: () => void;
  showDateRange?: boolean;
  start_date?: string;
  end_date?: string;
  onDateRangeChanged?: (start_date?: string, end_date?: string) => void;
  emptyMessage?: string;
  extraContent?: React.ReactNode;
}

const TripleColumnPage: React.FC<TripleColumnPageProps> = ({
  title,
  description,
  items,
  loading = false,
  onItemClick,
  onExport,
  onPrint = () => console.log(""),
  onExportImage,
  onExportPDF,
  onExportExcel,
  showDateRange = true,
  start_date,
  end_date,
  onDateRangeChanged,
  emptyMessage = 'No items found',
  extraContent,
}) => {
  const navigate = useNavigate();
  const { format } = useCurrency();
  const [startDate, setStartDate] = useState(start_date || '');
  const [endDate, setEndDate] = useState(end_date || '');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  const hasExportDropdown = !!(onExportImage || onExportPDF || onExportExcel);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Handle date range changes
  useEffect(() => {
    if (startDate && endDate && onDateRangeChanged) {
      // Only call onDateRangeChanged when both dates are selected
      onDateRangeChanged(startDate, endDate);
    }
  }, [startDate, endDate, onDateRangeChanged]);

  // Filter items by date range
  const filteredItems = items.filter(item => {
    // Date range filter
    if (startDate && endDate && item.date) {
      const itemDate = new Date(item.date);
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Set end date to end of day for inclusive filtering
      end.setHours(23, 59, 59, 999);

      return itemDate >= start && itemDate <= end;
    }

    // If no date range is selected, show all items
    return true;
  });

  // Calculate totals
  const totalIssueQuantity = filteredItems.reduce((sum, item) => sum + (item.issueQuantity || 0), 0);
  const totalIssueAmount = filteredItems.reduce((sum, item) => sum + (item.issueAmount || 0), 0);
  const totalReceiptQuantity = filteredItems.reduce((sum, item) => sum + (item.receiptQuantity || 0), 0);
  const totalReceiptAmount = filteredItems.reduce((sum, item) => sum + (item.receiptAmount || 0), 0);
  const totalBalanceQuantity = filteredItems.reduce((sum, item) => sum + (item.balanceQuantity || 0), 0);
  const totalBalanceAmount = filteredItems.reduce((sum, item) => sum + (item.balanceAmount || 0), 0);

  

    const formatNumber = (amount: number, unit?: string, isQuantity: boolean = false) => {
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: isQuantity ? 0 : 2,
      maximumFractionDigits: isQuantity ? 0 : 2
    });
    return unit ? `${formatted} ${unit}` : formatted;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Helper to display dash for empty values
        const displayValue = (value: number | undefined, isCurrency: boolean = false, unit?: string, isQuantity: boolean = false) => {
    if (value === undefined || value === 0) return '';
    return isCurrency ? format(value) : formatNumber(value, unit, isQuantity);
  };

    const getDisplayWithTitle = (value: number | undefined, isCurrency: boolean = false, unit?: string, isQuantity: boolean = false) => {
    if (value === undefined || value === 0) return { display: '', title: '' };
    const display = isCurrency ? format(value) : formatNumber(value, unit, isQuantity);
    return { display, title: display };
  };

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="p-6 pb-3 bg-light rounded-t-lg flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
          <div className="w-full sm:w-auto">
            <h3 className="text-xl">{title}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {description && (
                <p className="text-grey_dark text-sm">
                  {description}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-4 sm:mt-0 text-sm">
            {hasExportDropdown ? (
              <div className="relative" ref={exportDropdownRef}>
                <Button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  variant="google"
                  className="flex items-center text-grey_dark"
                >
                  <img
                    src={downloadIcon}
                    alt="Export"
                    className="object-contain w-6 h-6 mr-2"
                  />
                  Export
                </Button>
                {showExportMenu && (
                  <div className="absolute right-0 top-10 bg-light border border-grey_light rounded-lg shadow-lg z-50 min-w-[160px]">
                    {onExportImage && (
                      <button
                        onClick={() => { setShowExportMenu(false); onExportImage(); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors rounded-t-lg flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        Image (PNG)
                      </button>
                    )}
                    {onExportPDF && (
                      <button
                        onClick={() => { setShowExportMenu(false); onExportPDF(); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                        PDF
                      </button>
                    )}
                    {onExportExcel && (
                      <button
                        onClick={() => { setShowExportMenu(false); onExportExcel(); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-dark hover:bg-bg_grey_light transition-colors rounded-b-lg flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-grey_dark" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        Excel
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : onExport ? (
              <Button
                onClick={onExport}
                variant="google"
                className="flex items-center text-grey_dark"
              >
                <img
                  src={downloadIcon}
                  alt="Export"
                  className="object-contain w-6 h-6 mr-2"
                />
                Export
              </Button>
            ) : null}
            {onPrint && (
              <Button
                onClick={onPrint}
                variant="google"
                className="flex items-center text-grey_dark"
              >
                <img
                  src={printIcon}
                  alt="Print"
                  className="object-contain w-6 h-6 mr-2"
                />
                Print
              </Button>
            )}
          </div>
        </div>

        {/* Date Range Selector */}
        {showDateRange && (
          <div className="bg-light rounded-lg p-6 mb-2">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex justify-between">
                <div className='mr-2'>
                  <DatePicker
                    id="start-date"
                    value={startDate}
                    onChange={setStartDate}
                    label="Start Date"
                    type="date"
                  />
                </div>
                <div className='ml-2'>
                  <DatePicker
                    id="end-date"
                    value={endDate}
                    onChange={setEndDate}
                    label="End Date"
                    type="date"
                  />
                </div>
              </div>
              <div className="text-sm text-grey_dark">
                {filteredItems.length} of {items.length} entries
              </div>
            </div>
          </div>
        )}

        {/* Single Table with 4 Main Columns */}
        <div className="bg-light rounded-lg p-4 sm:p-6 mb-4 overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Main Headers */}
            <div className="grid grid-cols-10 gap-2 border-b border-grey_light text-grey_dark text-sm">
              <div className="col-span-1 border-r border-grey_light  py-2">Date</div>
              <div className="col-span-3 text-center border-r border-grey_light  py-2">Issue</div>
              <div className="col-span-3 text-center border-r border-grey_light  py-2">Receipt</div>
              <div className="col-span-3 text-center">Balance</div>
            </div>

            {/* Sub-headers for Quantity, Price, Amount */}
            <div className="grid grid-cols-10 gap-2 border-b border-grey_light text-grey_dark text-sm">
              <div className="col-span-1  py-2 border-r border-grey_light"></div>

              {/* Issue sub-columns */}
              <div className="col-span-1 text-right py-2">Qty</div>
              <div className="col-span-1 text-right py-2">Price</div>
              <div className="col-span-1 text-right py-2 border-r border-grey_light pr-2">Amt</div>

              {/* Receipt sub-columns */}
              <div className="col-span-1 text-right py-2">Qty</div>
              <div className="col-span-1 text-right py-2">Price</div>
              <div className="col-span-1 text-right py-2 border-r border-grey_light pr-2">Amt</div>

              {/* Balance sub-columns */}
              <div className="col-span-1 text-right py-2">Qty</div>
              <div className="col-span-1 text-right py-2">Price</div>
              <div className="col-span-1 text-right py-2">Amt</div>
            </div>

            {/* Table Body */}
            {filteredItems.length === 0 ? (
              <div className="text-center py-6 text-grey_dark text-sm">
                {emptyMessage}
              </div>
            ) : (
              <div>
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => onItemClick && onItemClick(item)}
                    className={`grid grid-cols-10 gap-2 hover:bg-bg_grey_light transition-colors duration-150 ${onItemClick ? 'cursor-pointer' : ''}`}
                  >
                    {/* Date Column */}
                    <div className="col-span-1 text-sm text-grey_dark border-r border-grey_light pr-2 py-2">
                      {formatDate(item.date)}
                    </div>

                    {/* Issue Column */}
                    <div className="col-span-1 text-sm text-right py-2">
                      {displayValue(item.issueQuantity, false, item.unit, true)}
                    </div>
                                        <div className="col-span-1 text-sm text-right py-2">
                      {(() => {
                        const { display, title } = getDisplayWithTitle(item.issuePrice, true);
                        return display ? (
                          <span className="transaction-figure" title={title}>
                            {display}
                          </span>
                        ) : '';
                      })()}
                    </div>
                                        <div className="col-span-1 text-sm text-right text-danger_dark border-r border-grey_light pr-2 py-2">
                      {(() => {
                        const { display, title } = getDisplayWithTitle(item.issueAmount, true);
                        return display ? (
                          <span className="transaction-figure" title={title}>
                            {display}
                          </span>
                        ) : '';
                      })()}
                    </div>

                    {/* Receipt Column */}
                    <div className="col-span-1 text-sm text-right py-2">
                      {displayValue(item.receiptQuantity, false, item.unit, true)}
                    </div>
                                        <div className="col-span-1 text-sm text-right py-2">
                      {(() => {
                        const { display, title } = getDisplayWithTitle(item.receiptPrice, true);
                        return display ? (
                          <span className="transaction-figure" title={title}>
                            {display}
                          </span>
                        ) : '';
                      })()}
                    </div>
                                        <div className="col-span-1 text-sm text-right text-primary_variant border-r border-grey_light pr-2 py-2">
                      {(() => {
                        const { display, title } = getDisplayWithTitle(item.receiptAmount, true);
                        return display ? (
                          <span className="transaction-figure" title={title}>
                            {display}
                          </span>
                        ) : '';
                      })()}
                    </div>

                    {/* Balance Column */}
                    <div className="col-span-1 text-sm text-right py-2">
                      {displayValue(item.balanceQuantity, false, item.unit, true)}
                    </div>
                                        <div className="col-span-1 text-sm text-right py-2">
                      {(() => {
                        const { display, title } = getDisplayWithTitle(item.balancePrice, true);
                        return display ? (
                          <span className="transaction-figure" title={title}>
                            {display}
                          </span>
                        ) : '';
                      })()}
                    </div>
                                        <div className="col-span-1 text-sm text-right py-2">
                      {(() => {
                        const { display, title } = getDisplayWithTitle(item.balanceAmount, true);
                        return display ? (
                          <span className="transaction-figure" title={title}>
                            {display}
                          </span>
                        ) : '';
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Totals Row */}
            {filteredItems.length > 0 && (
              <div className="border-t border-grey_light">
                <div className="grid grid-cols-10 gap-2">
                  <div className="col-span-1 text-sm text-right border-r border-grey_light pr-2">Total:</div>

                  {/* Issue Totals */}
                  <div className="col-span-1 text-sm text-right text-danger_dark py-2">
                    {formatNumber(totalIssueQuantity, undefined, true)}
                  </div>
                  <div className="col-span-1 text-sm text-right py-2"></div>
                                    <div className="col-span-1 text-sm text-right text-danger_dark border-r border-grey_light pr-2  py-2">
                                        <span className="transaction-figure" title={format(totalIssueAmount)}>
                      {format(totalIssueAmount)}
                    </span>
                  </div>

                  {/* Receipt Totals */}
                  <div className="col-span-1 text-sm text-right text-primary_variant  py-2">
                    {formatNumber(totalReceiptQuantity, undefined, true)}
                  </div>
                  <div className="col-span-1 text-sm text-right py-2"></div>
                                    <div className="col-span-1 text-sm text-right text-primary_variant border-r border-grey_light pr-2  py-2">
                                        <span className="transaction-figure" title={format(totalReceiptAmount)}>
                      {format(totalReceiptAmount)}
                    </span>
                  </div>

                  {/* Balance Totals */}
                  <div className="col-span-1 text-sm text-right py-2">
                    {formatNumber(totalBalanceQuantity, undefined, true)}
                  </div>
                  <div className="col-span-1 text-sm text-right py-2"></div>
                                    <div className="col-span-1 text-sm text-right py-2">
                                        <span className="transaction-figure" title={format(totalBalanceAmount)}>
                      {format(totalBalanceAmount)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Net Movement Summary */}
            {filteredItems.length > 0 && (
              <div className="border-t border-grey_light">
                <div className="grid grid-cols-10 gap-2">
                  <div className="col-span-4 text-sm text-right py-2">Net Movement:</div>
                  <div className="col-span-6 text-sm text-right py-2">
                                        <span 
                      className={`${totalIssueAmount - totalReceiptAmount >= 0 ? 'text-danger_dark' : 'text-primary_variant'} transaction-figure`}
                                            title={format(Math.abs(totalIssueAmount - totalReceiptAmount))}
                    >
                      {format(Math.abs(totalIssueAmount - totalReceiptAmount))}
                      {totalIssueAmount - totalReceiptAmount >= 0 ? ' (Net Issue)' : ' (Net Receipt)'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {extraContent}
      </div>
    </div>
  );
};

export default TripleColumnPage;